import { Box, Dialog, DialogActions, DialogContent } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import CustomButton from '../../components/custom/btn/CustomButton';
import { destructiveActionHintSx } from '../../components/custom/btn/customButtonStyles';
import CustomCancelButton from '../../components/custom/btn/CustomCancelButton';
import CustomSuccessButton from '../../components/custom/btn/CustomSuccessButton';
import {
	showErrorSnackbar,
	showSuccessSnackbar,
} from '../../components/custom/snackbar/snackbarSlice';
import { apiFetch } from '../../shared/apiClient';
import { getActiveSeasonId } from '../admin/seasons/seasonsSlice';
import { selectActiveSeasonId } from '../admin/seasons/selectors';
import { ADMIN_BUTTON_STACK_SX } from '../admin/adminPanelStyles';
import { playersStatsFullRecalculation } from './statsSlice';

// TODO: refactor this component

export default function StatsRecalculating({
	startLoading,
	stopLoading,
}: {
	startLoading: () => void;
	stopLoading: () => void;
}): JSX.Element {
	const dispatch = useAppDispatch();
	const activeSeasonId = useAppSelector(selectActiveSeasonId);
	const [openRecalculatePlayerStatsDialog, setOpenRecalculatePlayerStatsDialog] = useState(false);
	const [openRecalculateTeamStatsDialog, setOpenRecalculateTeamStatsDialog] = useState(false);
	const [openRecalculateBetTitlesStatsDialog, setOpenRecalculateBetTitlesStatsDialog] =
		useState(false);
	const [openRecalculateBetValuesStatsDialog, setOpenRecalculateBetValuesStatsDialog] =
		useState(false);
	const [openRecalculateGameweekStatsDialog, setOpenRecalculateGameweekStatsDialog] =
		useState(false);

	const handleSubmitFullRecalculation = useCallback(
		async (event?: React.FormEvent) => {
			setOpenRecalculatePlayerStatsDialog(false);
			event?.preventDefault();
			startLoading();
			if (activeSeasonId) {
				const dispatchResult = await dispatch(playersStatsFullRecalculation(activeSeasonId));

				if (playersStatsFullRecalculation.fulfilled.match(dispatchResult)) {
					dispatch(showSuccessSnackbar({ message: t('statsWasSuccessfullyRecalculated') }));
				}
				if (playersStatsFullRecalculation.rejected.match(dispatchResult)) {
					dispatch(showErrorSnackbar({ message: dispatchResult.error.message }));
				}
				stopLoading();
			}
		},
		[dispatch, activeSeasonId]
	);

	const handleSubmitStatsByTeamsRecalculation = useCallback(
		async (event?: React.FormEvent) => {
			setOpenRecalculateTeamStatsDialog(false);
			event?.preventDefault();
			startLoading();
			if (activeSeasonId) {
				try {
					let url = `${
						import.meta.env.VITE_PRODUCT_SERVER
					}/api/stats/season/${activeSeasonId}/recalculation/teams`;
					if (import.meta.env.VITE_PRODUCT_SERVER === 'localhost') {
						url = `/api/stats/season/${activeSeasonId}/recalculation/teams`;
					}
					const response = await apiFetch(`${url}`);

					if (response.ok) {
						dispatch(showSuccessSnackbar({ message: t('statsWasSuccessfullyRecalculated') }));
					} else {
						const errorMessage = await response.text();
						dispatch(
							showErrorSnackbar({ message: errorMessage || t('errorByStatsRecalculating') })
						);
					}
				} catch (error) {
					dispatch(showErrorSnackbar({ message: t('errorByStatsRecalculating') }));
				}
				stopLoading();
			}
		},
		[activeSeasonId]
	);

	const handleSubmitBetTitlesStatsRecalculation = useCallback(
		async (event?: React.FormEvent) => {
			setOpenRecalculateBetTitlesStatsDialog(false);
			event?.preventDefault();
			startLoading();
			if (activeSeasonId) {
				try {
					let url = `${
						import.meta.env.VITE_PRODUCT_SERVER
					}/api/stats/season/${activeSeasonId}/recalculation/bet-titles`;
					if (import.meta.env.VITE_PRODUCT_SERVER === 'localhost') {
						url = `/api/stats/season/${activeSeasonId}/recalculation/bet-titles`;
					}
					const response = await apiFetch(`${url}`);

					if (response.ok) {
						dispatch(showSuccessSnackbar({ message: t('statsWasSuccessfullyRecalculated') }));
					} else {
						const errorMessage = await response.text();
						dispatch(
							showErrorSnackbar({ message: errorMessage || t('errorByStatsRecalculating') })
						);
					}
				} catch (error) {
					dispatch(showErrorSnackbar({ message: t('errorByStatsRecalculating') }));
				}
				stopLoading();
			}
		},
		[activeSeasonId]
	);

	const handleSubmitBetValuesStatsRecalculation = useCallback(
		async (event?: React.FormEvent) => {
			setOpenRecalculateBetValuesStatsDialog(false);
			event?.preventDefault();
			startLoading();
			if (activeSeasonId) {
				try {
					let url = `${
						import.meta.env.VITE_PRODUCT_SERVER
					}/api/stats/season/${activeSeasonId}/recalculation/bet-values`;
					if (import.meta.env.VITE_PRODUCT_SERVER === 'localhost') {
						url = `/api/stats/season/${activeSeasonId}/recalculation/bet-values`;
					}
					const response = await apiFetch(`${url}`);

					if (response.ok) {
						dispatch(showSuccessSnackbar({ message: t('statsWasSuccessfullyRecalculated') }));
					} else {
						const { message }: { message?: string } = await response.json();
						dispatch(
							showErrorSnackbar({ message: message || t('errorByStatsRecalculating') })
						);
					}
				} catch (error) {
					dispatch(showErrorSnackbar({ message: t('errorByStatsRecalculating') }));
				}
				stopLoading();
			}
		},
		[activeSeasonId]
	);

	const handleSubmitGameweekStatsRecalculation = useCallback(
		async (event?: React.FormEvent) => {
			setOpenRecalculateGameweekStatsDialog(false);
			event?.preventDefault();
			startLoading();
			if (activeSeasonId) {
				try {
					let url = `${
						import.meta.env.VITE_PRODUCT_SERVER
					}/api/stats/season/${activeSeasonId}/recalculation/gameweeks`;
					if (import.meta.env.VITE_PRODUCT_SERVER === 'localhost') {
						url = `/api/stats/season/${activeSeasonId}/recalculation/gameweeks`;
					}
					const response = await apiFetch(`${url}`);

					if (response.ok) {
						dispatch(showSuccessSnackbar({ message: t('statsWasSuccessfullyRecalculated') }));
					} else {
						const errorMessage = await response.text();
						dispatch(
							showErrorSnackbar({ message: errorMessage || t('errorByStatsRecalculating') })
						);
					}
				} catch (error) {
					dispatch(showErrorSnackbar({ message: t('errorByStatsRecalculating') }));
				}
				stopLoading();
			}
		},
		[activeSeasonId]
	);

	const handleRecalculatePlayerStatsDialog = (): void => {
		setOpenRecalculatePlayerStatsDialog(!openRecalculatePlayerStatsDialog);
	};

	const handleRecalculateTeamStatsDialog = (): void => {
		setOpenRecalculateTeamStatsDialog(!openRecalculateTeamStatsDialog);
	};

	const handleRecalculateBetTitlesStatsDialog = (): void => {
		setOpenRecalculateBetTitlesStatsDialog(!openRecalculateBetTitlesStatsDialog);
	};

	const handleRecalculateBetValuesStatsDialog = (): void => {
		setOpenRecalculateBetValuesStatsDialog(!openRecalculateBetValuesStatsDialog);
	};

	const handleRecalculateGameweekStatsDialog = (): void => {
		setOpenRecalculateGameweekStatsDialog(!openRecalculateGameweekStatsDialog);
	};

	const handleCloseDialog = (): void => {
		setOpenRecalculatePlayerStatsDialog(false);
		setOpenRecalculateTeamStatsDialog(false);
		setOpenRecalculateBetTitlesStatsDialog(false);
		setOpenRecalculateBetValuesStatsDialog(false);
		setOpenRecalculateGameweekStatsDialog(false);
	};

	useEffect(() => {
		if (!activeSeasonId) {
			dispatch(getActiveSeasonId());
		}
	}, [dispatch]);

	return (
		<Box sx={ADMIN_BUTTON_STACK_SX}>
			<CustomButton
				buttonColor="error"
				buttonVariant="outlined"
				onClick={handleRecalculatePlayerStatsDialog}
				buttonText={t('mainStatsRecalculating')}
			/>
			<CustomButton
				buttonColor="error"
				buttonVariant="outlined"
				onClick={handleRecalculateTeamStatsDialog}
				buttonText={t('teamStatsRecalculating')}
			/>
			<CustomButton
				buttonColor="error"
				buttonVariant="outlined"
				onClick={handleRecalculateBetTitlesStatsDialog}
				buttonText={t('betTitlesStatsRecalculating')}
			/>
			<CustomButton
				buttonColor="error"
				buttonVariant="outlined"
				onClick={handleRecalculateBetValuesStatsDialog}
				buttonText={t('betValuesStatsRecalculating')}
			/>
			<CustomButton
				buttonColor="error"
				buttonVariant="outlined"
				onClick={handleRecalculateGameweekStatsDialog}
				buttonText={t('gameweekStatsRecalculating')}
			/>
			<Dialog open={openRecalculatePlayerStatsDialog} onClose={handleCloseDialog}>
				<DialogContent>
					<Box sx={{ fontWeight: '600', fontSize: '1rem' }}>
						{t('mainStatsWillBeRecalculated')}
						<Box sx={destructiveActionHintSx}>{t('thisActionCannotBeCanceled')}</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<CustomCancelButton onClick={handleCloseDialog} />
					<CustomSuccessButton onClick={handleSubmitFullRecalculation} />
				</DialogActions>
			</Dialog>

			<Dialog open={openRecalculateTeamStatsDialog} onClose={handleCloseDialog}>
				<DialogContent>
					<Box sx={{ fontWeight: '600', fontSize: '1rem' }}>
						{t('teamStatsWillBeRecalculated')}
						<Box sx={destructiveActionHintSx}>{t('thisActionCannotBeCanceled')}</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<CustomCancelButton onClick={handleCloseDialog} />
					<CustomSuccessButton onClick={handleSubmitStatsByTeamsRecalculation} />
				</DialogActions>
			</Dialog>

			<Dialog open={openRecalculateGameweekStatsDialog} onClose={handleCloseDialog}>
				<DialogContent>
					<Box sx={{ fontWeight: '600', fontSize: '1rem' }}>
						{t('gameweekStatsWillBeRecalculated')}
						<Box sx={destructiveActionHintSx}>{t('thisActionCannotBeCanceled')}</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<CustomCancelButton onClick={handleCloseDialog} />
					<CustomSuccessButton onClick={handleSubmitGameweekStatsRecalculation} />
				</DialogActions>
			</Dialog>

			<Dialog open={openRecalculateBetTitlesStatsDialog} onClose={handleCloseDialog}>
				<DialogContent>
					<Box sx={{ fontWeight: '600', fontSize: '1rem' }}>
						{t('betTitlesStatsWillBeRecalculated')}
						<Box sx={destructiveActionHintSx}>{t('thisActionCannotBeCanceled')}</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<CustomCancelButton onClick={handleCloseDialog} />
					<CustomSuccessButton onClick={handleSubmitBetTitlesStatsRecalculation} />
				</DialogActions>
			</Dialog>

			<Dialog open={openRecalculateBetValuesStatsDialog} onClose={handleCloseDialog}>
				<DialogContent>
					<Box sx={{ fontWeight: '600', fontSize: '1rem' }}>
						{t('betValuesStatsWillBeRecalculated')}
						<Box sx={destructiveActionHintSx}>{t('thisActionCannotBeCanceled')}</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<CustomCancelButton onClick={handleCloseDialog} />
					<CustomSuccessButton onClick={handleSubmitBetValuesStatsRecalculation} />
				</DialogActions>
			</Dialog>
		</Box>
	);
}
